var _ = require('underscore');
var TorsoFormView = require('backbone-torso/modules/FormView');
var TorsoCollection = require('backbone-torso/modules/Collection');
var routerUtils = require('./../../utils/routerUtils');
var stringUtils = require('./../../utils/stringUtils');

var TableView = require('./table-view/TableView');
var ShownEntriesView = require('./shown-entries/ShownEntriesView');
var SearchView = require('./search/SearchView');
var TableState = require('./TableState');
var DropdownView = require('./../dropdown/DropdownView');
var PaginationView = require('./../pagination/PaginationView');

var SORT_PROPERTY_PREFIX = 'sort.';

/**
 * View for the overall (read: parent) BrecTable Widget
 *
 * @class BrecTableView
 * @param [options] {Object}
 *   @param [options.widgetOptions=this.widgetOptions] {Object} the options for enabling / configuring
 *                                                              different components of the table widget.
 * @param {options} Object that has options passed into it
 *
 */
var BrecTableView = (TorsoFormView.extend({

  /**
   * @property widgetOptions {Object} the options used to configure the brec table.
   */
  widgetOptions: {
    page: 1,
    entriesPerPage: 10
  },

  /**
   * The nested cell object to use to hold table state.  View property so it can be overridden.
   * @property TableState {Torso.NestedCell} the class to use to generate the instance of the table state object to use
   *                                         for this table.
   */
  TableState: TableState,

  events: {
    'click': '_closeDropdown'
  },

  /**
   * @method constructor
   * @param [options] {Object}
   *   @param [options.widgetOptions=this.widgetOptions] {Object} the options for enabling / configuring
   *                                                              different components of the table widget.
   *   @param [options.state] {Object} initial state (generally taken from query parameters) to be parsed by setState().
   * @override
   *
   * Override the constructor rather than Initialize so that
   * we can still easily override initialize when we extend
   * this view.
   */
  constructor: function(options) {
    options = options || {};
    this.widgetOptions = _.defaults({}, options.widgetOptions, this.widgetOptions);
    this.tableState = new this.TableState({
      active: false,
      loading: true
    });

    var initialState = _.defaults({}, options.state, this.widgetOptions);
    this.setState(initialState);

    TorsoFormView.apply(this, arguments);

    // set defaults after applying the parent constructor so subclasses can swap out the tableState for other classes in their initialize.
    this._bindOptionalComponents();
  },

  /**
   * @method initialize
   * @override
   */
  initialize: function() {
    this.listenTo(this.tableState, 'change:entriesPerPage', this._onSetEntriesPerPage);
    this.listenTo(this.tableState, 'change:columnSorting', this._onColumnSorting);
    this.listenTo(this.tableState, 'change:search', this._onSetSearch);
    this.listenTo(this.tableState, 'change:page', this._onSetPage);

    // Setup and configuration complete we can finally start fetching.
    this.startFetching();
  },

  /**
   * @method prepare
   * @override
   */
  prepare: function() {
    var templateContext = TorsoFormView.prototype.prepare.apply(this);
    templateContext.tableState = this.tableState.toJSON();
    return templateContext;
  },

  /**
   * Public method to set entries per page, sets it on the bus for all the listeners to pick up.
   * @method setEntriesPerPage
   * @param entriesPerPage {String|Number} the number of entries to display per page.
   */
  setEntriesPerPage: function(entriesPerPage) {
    // If a entriesPerPage is being unset then revert it to the default value.
    if (_.isUndefined(entriesPerPage)) {
      entriesPerPage = this.widgetOptions.entriesPerPage;
    }
    // Only set it if its a number.  Also convert it to a Number if it is a string.
    if (_.isFinite(entriesPerPage)) {
      entriesPerPage = Number(entriesPerPage);
    }
    this.tableState.set('entriesPerPage', entriesPerPage);
  },

  /**
   * This is a bus listener that will get called whenever the entries per page changes.
   * Calls the overridable _setEntriesPerPage internally.
   * @method _onSetEntriesPerPage
   * @private
   */
  _onSetEntriesPerPage: function() {
    var entriesPerPage = this.tableState.get('entriesPerPage');
    routerUtils.updateHashQueryString('entriesPerPage', entriesPerPage);
    if (_.isFunction(this._setEntriesPerPage)) {
      this._setEntriesPerPage(entriesPerPage);
    }
  },

  /**
   * Extension hook that is called when the number of entries per page changes.
   * Other views associated with this widget can listen for this event on the
   * event bus as 'change:entriesPerPage').
   * @method _setEntriesPerPage
   * @param entriesPerPage {String|Number} the number of items to display per page.
   * @private
   */
  _setEntriesPerPage: _.noop,

  /**
   * Public method to set the ordering map, sets it on the bus for all the listeners to pick up.
   * @method setColumnSorting
   * @param columnSorting {Object} the map used to sort
   */
  setColumnSorting: function(columnSorting) {
    if (!_.isEmpty(columnSorting)) {
      this.tableState.set('columnSorting', columnSorting);
    }
  },

  /**
   * This is a bus listener that will get called whenever the entries per page changes.
   * Calls the overridable _setColumnSorting internally.
   * @method _onSetColumnSorting
   * @private
   */
  _onColumnSorting: function() {
    var columnSorting = this.tableState.get('columnSorting');
    var hash = window.location.hash;
    if (!_.isEmpty(columnSorting)) {
      _.each(columnSorting, function(direction, sortColumnName) {
        sortColumnName = SORT_PROPERTY_PREFIX + sortColumnName;
        hash = routerUtils.addQueryParameterToHash(sortColumnName, direction, hash);
      });
    }
    window.location.hash = hash;
    if (_.isFunction(this._setColumnSorting)) {
      this._setColumnSorting(columnSorting);
    }
  },

  /**
   * Extension hook that is called when the number of entries per page changes.
   * Other views associated with this widget can listen for this event on the
   * event bus as 'change:columnSorting').
   * @method _setColumnSorting
   * @param columnSorting {String|Number} the number of items to display per page.
   * @private
   */
  _setColumnSorting: _.noop,

  /**
   * Public method to set the search string, sets it on the bus for all the listeners to pick up.
   * @method setSearch
   * @param search {String} the query string to search against.
   */
  setSearch: function(search) {
    this.tableState.set('search', search);
  },

  /**
   * This is a bus listener that will get called whenever the search string changes.
   * Calls the overridable _setSearch internally.
   * @method _onSetSearch
   * @private
   */
  _onSetSearch: function() {
    var search = this.tableState.get('search');
    routerUtils.updateHashQueryString('search', search);
    if (_.isFunction(this._setSearch)) {
      this._setSearch(search);
    }
  },

  /**
   * Extension hook that is called when the search string changes. Other views
   * associated with this widget can listen for this event on the event bus as
   * 'change:search').
   * @method _setSearch
   * @param search {String} the query string to search against.
   * @private
   */
  _setSearch: _.noop,

  /**
   * Public method to set the page to display, sets it on the bus for all the listeners to pick up.
   * @method setPage
   * @param page {String|Number} the page of the table to display.
   */
  setPage: function(page) {
    // If a page is being unset then revert it to the default value.
    if (_.isUndefined(page)) {
      page = this.widgetOptions.page;
    }
    // Only set it if its a number.  Also convert it to a Number if it is a string.
    if (_.isFinite(page)) {
      page = Number(page);
      this.tableState.set('page', page);
    }
  },

  /**
   * This is a bus listener that will get called whenever the page changes.
   * Calls the overridable _setPage internally.
   * @method _onSetPage
   * @private
   */
  _onSetPage: function() {
    var page = this.tableState.get('page');
    routerUtils.updateHashQueryString('page', page);
    if (_.isFunction(this._setPage)) {
      this._setPage(page);
    }
  },

  /**
   * Extension hook that is called when the page changes. Other views
   * associated with this widget can listen for this event on the event bus as
   * 'change:page').
   * @method _setPage
   * @param page {String|Number} the page to set.
   * @private
   */
  _setPage: _.noop,

  /**
   * Public method to start the fetching process for the data of this widget.
   * Alias for setActive(true);
   * @method startFetching
   */
  startFetching: function() {
    this.setActive(true);
  },

  /**
   * Public method to stop the fetching proces for the data of this widget.
   * Alias for setActive(false);
   * @method stopFetching
   */
  stopFetching: function() {
    this.setActive(false);
  },

  /**
   * Public method to set the active status of the table widget.
   * @method setActive
   * @param active {Boolean} true if the widget should be active (and fetch data) or false if inactive (not fetching data).
   */
  setActive: function(active) {
    this.tableState.set('active', active);
  },

  /**
   * Public method to set the state of the widget, sets it on the bus for all the listeners to pick up.
   * @method setState
   * @param [state] {Object} the state to update.  This could be a partial state, assume values not set are ignored.
   *   @param [state.page] {String} the current page of data to display.
   *   @param [state.entriesPerPage] {String} the number of entries per page to display.
   *   @param [state.search] {String} the search string that the user typed in.
   *   @param [state.columnSorting] {String} JSON representation of the column sorting map.
   */
  setState: function(state) {
    state = state || {};

    // Only activate fetching if the widget is active when we set the state.
    var isActive = this.tableState.get('active');

    var newStateString = JSON.stringify(state);
    // Avoid setting any state (and forcing a fetch) unless it has changed.
    if (newStateString === this.__currentStateString) {
      if (isActive) {
        this.startFetching(); // make sure the table is active.
      }
      return;
    }
    // Keep track of current state in a way that it can be compared to new state.
    // This property should never be used for anything else.
    this.__currentStateString = newStateString;

    // Stop fetching to prevent multiple fetches from multiple individual properties changing.
    this.stopFetching();
    this.setPage(state.page);
    this.setEntriesPerPage(state.entriesPerPage);
    this.setSearch(state.search);
    // Column sorting uses prefixed keys to support multi-sorting.
    var columnSorting = {};
    _.each(state, function(direction, urlSortPropertyName) {
      if (stringUtils.startsWith(urlSortPropertyName, SORT_PROPERTY_PREFIX)) {
        var columnSortProperty = urlSortPropertyName.substring(SORT_PROPERTY_PREFIX.length);
        columnSorting[columnSortProperty] = direction;
      }
    });
    if (_.isEmpty(columnSorting)) {
      columnSorting = state.columnSorting;
    }
    this.setColumnSorting(columnSorting);
    if (_.isFunction(this._setState)) {
      this._setState(state);
    }
    if (isActive) {
      this.startFetching();
    }
  },

  /**
   * Extension hook that is called when the state changes. Other views
   * associated with this widget can listen for this event on the event bus as
   * 'change:state').
   * @method _setState
   * @param state {Object} the state to update.  This could be a partial state, assume values not set are ignored.
   * @private
   */
  _setState: _.noop,

  /**
   * @method _bindOptionalComponents
   * @private
   *
   * Use the options passed into this view to determine
   * whether the child components are active. We also
   * pass the tableState to them for ease of communication
   * between these widgets, and with this parent view.
   */
  _bindOptionalComponents: function() {
    var defaultTableOptions = {
      active: true,
      className: '',
      TableView: TableView,
      tableView: null,
      rowTemplate: null,
      rowView: null,
      tableTemplate: null
    };
    var tableOptions = _.defaults({}, this.widgetOptions.table, defaultTableOptions);
    var tableDomId = '';
    if (tableOptions.active) {
      // Defaults for the table view is expensive (creating a view) so we do that only if not passed in.
      this._tableView = tableOptions.tableView;
      if (!this._tableView) {
        var tableViewOptions = {
          tableState: this.tableState,
          tableOptions: tableOptions,
          template: tableOptions.tableTemplate,
          itemView: tableOptions.rowView,
          className: tableOptions.TableView.prototype.className + ' ' + tableOptions.className
        };
        // Avoid setting itemContext to { template: null }
        if (tableOptions.rowTemplate) {
          tableViewOptions.itemContext = {
            template: tableOptions.rowTemplate
          };
        }
        if (!_.isEmpty(tableOptions.data)) {
          tableViewOptions.collection = new TorsoCollection(tableOptions.data);
        }
        this._tableView = new tableOptions.TableView(tableViewOptions);
      }
      tableDomId = _.result(this._tableView, 'id');
    }

    var defaultShownEntriesOptions = {
      active: false,
      className: '',
      ShownEntriesView: ShownEntriesView,
      shownEntriesView: null,
      ariaControls: tableDomId,
      totalEntries: null
    };
    var shownEntriesOptions = _.defaults({}, this.widgetOptions.shownEntries, defaultShownEntriesOptions);
    if (shownEntriesOptions.active) {
      // Defaults for the show entries view is expensive (creating a view) so we do that only if not passed in.
      this._shownEntriesView = shownEntriesOptions.shownEntriesView;
      if (!this._shownEntriesView) {
        this._shownEntriesView = new shownEntriesOptions.ShownEntriesView({
          tableState: this.tableState,
          className: shownEntriesOptions.ShownEntriesView.prototype.className + ' ' + shownEntriesOptions.className,
          ariaControls: shownEntriesOptions.ariaControls,
          totalEntries: shownEntriesOptions.totalEntries,
          filterOptions: shownEntriesOptions.filterOptions
        });
      }
    }

    var defaultSearchOptions = {
      active: false,
      tagName: 'form',
      className: '',
      SearchView: SearchView,
      searchView: null,
      ariaControls: tableDomId
    };
    var searchOptions = _.defaults({}, this.widgetOptions.searchWidget, defaultSearchOptions);
    if (searchOptions.active) {
      // Defaults for the search view is expensive (creating a view) so we do that only if not passed in.
      this._searchView = searchOptions.searchView;
      if (!this._searchView) {
        this._searchView = new searchOptions.SearchView({
          tableState: this.tableState,
          placeholderMessageKey: searchOptions.placeholderMessageKey,
          tagName: searchOptions.tagName,
          className: searchOptions.SearchView.prototype.className + ' ' + searchOptions.className,
          ariaControls: searchOptions.ariaControls
        });
      }
    }

    var defaultDropdownFilterOptions = {
      active: false,
      className: '',
      DropdownView: DropdownView,
      dropdownFilterView: null,
      dropdownConfig: null,
      ariaControls: tableDomId
    };
    var dropdownFilterOptions = _.defaults({}, this.widgetOptions.dropdownFilter, defaultDropdownFilterOptions);
    if (dropdownFilterOptions.active) {
      // Defaults for the dropdown filter view is expensive (creating a view) so we do that only if not passed in.
      this._dropdownFilterView = dropdownFilterOptions.dropdownFilterView;
      if (!this._dropdownFilterView) {
        this._dropdownFilterView = new dropdownFilterOptions.DropdownView({
          tableState: this.tableState,
          dropdownConfig: dropdownFilterOptions.dropdownConfig,
          className: dropdownFilterOptions.DropdownView.prototype.className + ' ' + dropdownFilterOptions.className,
          ariaControls: dropdownFilterOptions.ariaControls
        });
      }
    }

    // Assume pagination is active by default, otherwise assume it is specified.
    var defaultPaginationFilterOptions = {
      active: true,
      className: '',
      PaginationView: PaginationView,
      paginationView: null,
      ariaControls: tableDomId
    };
    var paginationFilterOptions = _.defaults({}, this.widgetOptions.pagination, defaultPaginationFilterOptions);
    if (paginationFilterOptions.active) {
      // Defaults for the pagination view is expensive (creating a view) so we do that only if not passed in.
      this._paginationView = paginationFilterOptions.paginationView;
      if (!this._paginationView) {
        this._paginationView = new paginationFilterOptions.PaginationView({
          tableState: this.tableState,
          className: paginationFilterOptions.PaginationView.prototype.className + ' ' + paginationFilterOptions.className,
          ariaControls: paginationFilterOptions.ariaControls
        });
      }
    }
  },

  /**
   * Clicking outside the filter dropdowns should close them.
   * @method _closeDropdown
   * @private
   */
  _closeDropdown: function() {
    if (this._dropdownFilterView) {
      this._dropdownFilterView.closeDropdown();
    }
  },

  /**
   * @method attachTrackedViews
   * @override
   *
   * Attach the enabled compoents for this table.
   */
  attachTrackedViews: function() {
    if (this._tableView) {
      this.attachView('brec-table', this._tableView);
    }

    if (this._shownEntriesView) {
      this.attachView('shown-entries-filter', this._shownEntriesView);
    }

    if (this._searchView) {
      this.attachView('search-view', this._searchView);
    }

    if (this._dropdownFilterView) {
      this.attachView('dropdown-filter-view', this._dropdownFilterView);
    }

    if (this._paginationView) {
      this.attachView('pagination', this._paginationView);
    }
  }
}));

module.exports = BrecTableView;
